<template>
  <v-row>
    <v-col cols="12">
      <!-- Header Section -->
      <v-row justify="space-between" align="center">
        <v-col cols="6">
          <h1>Блоки</h1>
        </v-col>
        <v-col cols="3" class="search-col">
          <v-text-field
              v-model="search"
              prepend-icon="mdi-magnify"
              label="Поиск"
              single-line
              hide-details
              clearable
          />
        </v-col>
      </v-row>
    </v-col>

    <v-col cols="12">
      <!-- Filters -->
      <v-row>
        <v-col cols="12" sm="6" md="3">
          <v-select
              v-model="filterOrientation"
              :items="orientationOptions"
              label="Ориентация"
              outlined
              dense
              clearable
          />
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-select
              v-model="filterActive"
              :items="activeOptions"
              label="Статус"
              outlined
              dense
              clearable
          />
        </v-col>

        <v-spacer />

        <v-col cols="auto" class="text-right">
          <v-btn
              color="primary"
              @click="navigateToCreate"
          >
            <v-icon left>mdi-plus</v-icon>
            Создать
          </v-btn>
        </v-col>
      </v-row>

      <!-- Data Table -->
      <v-data-table
          :headers="headers"
          :items="filteredItems"
          :search="search"
          :loading="loading"
          :options.sync="options"
          :footer-props="footerProps"
          :header-props="{ sortIcon: 'icon-custom-arrow' }"
          :items-per-page="-1"
          no-data-text="Пусто"
          no-results-text="По заданному запросу ничего не найдено"
          loading-text="Загрузка... Подождите пожалуйста"
      >
        <!-- Icon Column -->
        <template #item.icon="{ item }">
          <img
              v-if="item.icon"
              :src="item.icon"
              width="36"
              height="36"
              style="object-fit:contain; display:block;"
          />
        </template>

        <!-- Orientation Column -->
        <template #item.orientation="{ item }">
          <v-chip
              small
              :color="item.orientation === 'horizontal' ? 'blue' : 'purple'"
              dark
          >
            <v-icon small left>
              {{ item.orientation === 'horizontal' ? 'mdi-arrow-left-right' : 'mdi-arrow-up-down' }}
            </v-icon>
            {{ item.orientation === 'horizontal' ? 'Горизонтальный' : 'Вертикальный' }}
          </v-chip>
        </template>

        <!-- Active Status Column -->
        <template #item.is_active="{ item }">
          <v-chip
              small
              :color="item.is_active ? 'success' : 'grey'"
              dark
          >
            {{ item.is_active ? 'Активен' : 'Неактивен' }}
          </v-chip>
        </template>

        <!-- Products Count Column -->
        <template #item.products_count="{ item }">
          <v-chip small>
            {{ item.products_count || 0 }} товаров
          </v-chip>
        </template>

        <!-- Sort Order Column -->
        <template #item.sort_order="{ item }">
          <div class="d-flex align-center">
            <v-btn
                icon
                x-small
                @click="changeSortOrder(item, 'up')"
                :disabled="isFirstItem(item)"
            >
              <v-icon small>mdi-arrow-up</v-icon>
            </v-btn>
            <span class="mx-2">{{ item.sort_order }}</span>
            <v-btn
                icon
                x-small
                @click="changeSortOrder(item, 'down')"
                :disabled="isLastItem(item)"
            >
              <v-icon small>mdi-arrow-down</v-icon>
            </v-btn>
          </div>
        </template>

        <!-- Actions Column -->
        <template #item.actions="{ item }">
          <div class="text-right">
            <v-menu offset-y>
              <template #activator="{ on, attrs }">
                <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                >
                  <v-icon small>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>

              <v-list>
                <v-list-item @click="navigateToEdit(item.id)">
                  <v-list-item-icon>
                    <v-icon>mdi-pencil</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Редактировать</v-list-item-title>
                </v-list-item>

                <v-list-item @click="duplicateBlock(item)">
                  <v-list-item-icon>
                    <v-icon>mdi-content-copy</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Дублировать</v-list-item-title>
                </v-list-item>

                <v-list-item @click="toggleActive(item)">
                  <v-list-item-icon>
                    <v-icon>{{ item.is_active ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    {{ item.is_active ? 'Деактивировать' : 'Активировать' }}
                  </v-list-item-title>
                </v-list-item>

                <v-list-item @click="openDeleteDialog(item)">
                  <v-list-item-icon>
                    <v-icon color="error">mdi-delete</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Удалить</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>
      </v-data-table>
    </v-col>

    <!-- Loading Overlay -->
    <v-overlay :value="overlay" :z-index="4" />

    <!-- Delete Confirmation Dialog -->
    <ConfirmDialog
        v-model="deleteDialog.show"
        :title="deleteDialog.title"
        :message="deleteDialog.message"
        @confirm="handleDelete"
        @cancel="closeDeleteDialog"
    />

    <!-- Snackbar for notifications -->
    <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        top
        right
    >
      {{ snackbar.message }}
      <template #action="{ attrs }">
        <v-btn
            text
            v-bind="attrs"
            @click="snackbar.show = false"
        >
          Закрыть
        </v-btn>
      </template>
    </v-snackbar>
  </v-row>
</template>

<script>
import BlockService from '@/api/BlockService'
import ConfirmDialog from '@/components/ConfirmDialog.vue'

export default {
  name: 'BlockIndex',

  components: {
    ConfirmDialog
  },

  data() {
    return {
      // Selection and filters
      search: '',
      filterOrientation: null,
      filterActive: null,

      // Filter options
      orientationOptions: [
        { text: 'Все', value: null },
        { text: 'Горизонтальный', value: 'horizontal' },
        { text: 'Вертикальный', value: 'vertical' }
      ],
      activeOptions: [
        { text: 'Все', value: null },
        { text: 'Активные', value: true },
        { text: 'Неактивные', value: false }
      ],

      // UI state
      loading: true,
      overlay: false,

      // Delete dialog state
      deleteDialog: {
        show: false,
        title: 'Подтвердить удаление',
        message: 'Вы уверены, что хотите удалить этот блок?',
        itemToDelete: null
      },

      // Snackbar state
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 4000
      },

      // Table configuration
      options: {},
      footerProps: {
        itemsPerPageText: 'Элементов на страницу',
        pageText: '{0}-{1} из {2}',
        itemsPerPageAllText: 'Все',
        itemsPerPageOptions: [-1]
      },

      // Table headers
      headers: [
        {
          text: 'Иконка',
          value: 'icon',
          sortable: false,
          width: '80px',
          align: 'center'
        },
        {
          text: 'Название',
          value: 'name',
          sortable: true
        },
        {
          text: 'Ориентация',
          value: 'orientation',
          sortable: true,
        },
        {
          text: 'Товаров',
          value: 'products_count',
          sortable: true,
        },
        {
          text: 'Статус',
          value: 'is_active',
          sortable: true,
        },
        {
          text: 'Порядок',
          value: 'sort_order',
          sortable: true,
          width: '150px'
        },
        {
          text: 'Действия',
          value: 'actions',
          sortable: false,
          width: '120px',
        }
      ],

      // Data
      items: []
    }
  },

  computed: {
    filteredItems() {
      let filtered = [...this.items]

      if (this.filterOrientation) {
        filtered = filtered.filter(item => item.orientation === this.filterOrientation)
      }

      if (this.filterActive !== null) {
        filtered = filtered.filter(item => item.is_active === this.filterActive)
      }

      return filtered
    },

    sortedItems() {
      return [...this.filteredItems].sort((a, b) => a.sort_order - b.sort_order)
    }
  },

  watch: {
    options: {
      handler() {
        this.fetchData()
      },
      deep: true
    }
  },

  mounted() {
    this.fetchData()
  },

  methods: {
    // Navigation methods
    navigateToCreate() {
      this.$router.push({ name: 'block-create' })
    },

    navigateToEdit(id) {
      this.$router.push({
        name: 'block-edit',
        params: { id }
      })
    },

    // Dialog methods
    openDeleteDialog(item) {
      this.deleteDialog.itemToDelete = item
      this.deleteDialog.show = true
    },

    closeDeleteDialog() {
      this.deleteDialog.show = false
      this.deleteDialog.itemToDelete = null
    },

    // CRUD operations
    async fetchData() {
      this.loading = true

      try {
        const response = await BlockService.list()
        this.items = response.data.data.map(item => ({
          ...item,
          products_count: item.products?.length || 0
        }))
      } catch (error) {
        this.handleError('Ошибка при загрузке данных', error)
      } finally {
        this.loading = false
      }
    },

    async handleDelete() {
      if (!this.deleteDialog.itemToDelete) return

      try {
        await BlockService.remove(this.deleteDialog.itemToDelete.id)
        this.showSuccess('Блок успешно удален')
        await this.fetchData()
      } catch (error) {
        this.handleError('Ошибка при удалении блока', error)
      } finally {
        this.closeDeleteDialog()
      }
    },

    async toggleActive(item) {
      try {
        await BlockService.update(
            item.id,
            BlockService.toPayload(item, { is_active: !item.is_active })
        )
        this.showSuccess(
            item.is_active ? 'Блок деактивирован' : 'Блок активирован'
        )
        await this.fetchData()
      } catch (error) {
        this.handleError('Ошибка при изменении статуса', error)
      }
    },

    async duplicateBlock(item) {
      try {
        await BlockService.create(
            BlockService.toPayload(item, {
              name: `${item.name} (копия)`,
              name_ua: `${item.name_ua} (копія)`,
              is_active: false,
              sort_order: item.sort_order + 1
            })
        )
        this.showSuccess('Блок успешно дублирован')
        await this.fetchData()
      } catch (error) {
        this.handleError('Ошибка при дублировании блока', error)
      }
    },

    // Sort order methods
    isFirstItem(item) {
      const sorted = this.sortedItems
      return sorted[0]?.id === item.id
    },

    isLastItem(item) {
      const sorted = this.sortedItems
      return sorted[sorted.length - 1]?.id === item.id
    },

    async changeSortOrder(item, direction) {
      const sorted = this.sortedItems
      const currentIndex = sorted.findIndex(i => i.id === item.id)

      if (currentIndex === -1) return

      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1

      if (newIndex < 0 || newIndex >= sorted.length) return

      const itemsToUpdate = [
        { id: sorted[currentIndex].id, sort_order: sorted[newIndex].sort_order },
        { id: sorted[newIndex].id, sort_order: sorted[currentIndex].sort_order }
      ]

      try {
        await BlockService.updateSortOrder({ items: itemsToUpdate })
        await this.fetchData()
      } catch (error) {
        this.handleError('Ошибка при изменении порядка', error)
      }
    },

    // Utility methods
    showSnackbar(message, color = 'success') {
      this.snackbar.message = message
      this.snackbar.color = color
      this.snackbar.show = true
    },

    handleError(message, error) {
      console.error(message, error)
      this.showSnackbar(message, 'error')
    },

    showSuccess(message) {
      this.showSnackbar(message, 'success')
    }
  }
}
</script>

<style lang="scss" scoped>
.v-btn--fab.v-size--x-small {
  width: 20px;
  height: 20px;

  .mdi::before {
    font-size: 12px;
  }
}
</style>